import { appText as text } from './app.text.js';
import type { AuthSession, Board, Project, ProjectList } from '@aimo/contracts/web';
import { ApiClient, ApiFailure } from './api.js';
import { errorMessage } from './design-canvas/error-copy.js';
import { element, field } from './dom.js';
import { deviceAuthorizationId, googleLoginButton, loginErrorText, showDeviceAuthorization } from './auth-flow.js';
import { readWorkbenchLocation, workbenchUrl } from './workbench-location.js';
import { AppRoot, type CanvasHandle } from './app-root.js';
import { applyDocumentLocale } from './i18n.js';
import { playStitchTransition } from './stitch-transition.js';
import { openArtifactViewer } from './design-canvas/artifact-viewer.js';

/**
 * The application shell: sign-in, device authorization, the project home and the project canvas.
 * The canvas is the only project workspace; there is no parallel conversation panel.
 */
const api = new ApiClient();
const mount = document.querySelector<HTMLElement>('#app')!;
const state = { projects: [] as Project[], project: null as Project | null, boardId: null as string | null };
type PendingStart = { kind: 'create'; name: string; intent?: string } | { kind: 'open'; projectId: string };
const PENDING_START_KEY = 'aimo.pendingStart';
let starting = false;
/** The home page and the project canvas share this one React root. */
const appRoot = new AppRoot();
let designCanvas: CanvasHandle | undefined;
let projectEpoch = 0;

const page = element('main', 'workspace');
const designCanvasHost = element('div', 'design-canvas-host');
page.append(designCanvasHost);
const notice = element('div', 'notice'); notice.hidden = true; notice.setAttribute('role', 'status');
notice.title = text.dismissNotice; notice.addEventListener('click', () => { notice.hidden = true; });

function showNotice(message: string, kind: 'error' | 'info' = 'error'): void {
  notice.textContent = message; notice.dataset.kind = kind; notice.hidden = false;
  if (!notice.isConnected) mount.prepend(notice);
}
function report(error: unknown): void { showNotice(error instanceof Error ? errorMessage(error) : text.incompleteAction); }

function disposeCanvas(): void { designCanvas?.dispose(); designCanvas = undefined; }

async function mountProjectCanvas(epoch: number, project: Project, boardId: string, initialIntent?: string): Promise<void> {
  const { createCanvasView } = await import('./canvas-view.js');
  if (epoch !== projectEpoch) return;
  disposeCanvas();
  mount.replaceChildren(notice, page);
  const handle: { current?: CanvasHandle } = {};
  designCanvas = handle.current = appRoot.showCanvas(designCanvasHost, createCanvasView(api, project.projectId, boardId, {
    inspectArtifact: artifact => {
      const controller = handle.current?.controller;
      if (!controller || designCanvas !== handle.current || artifact.projectId !== project.projectId) return;
      openArtifactViewer(controller, artifact);
    },
    onBackToProjects: () => { history.pushState(null, '', '#projects'); void showUnifiedHome(); },
    projectName: project.name,
    ...(initialIntent ? { initialIntent } : {}),
  }));
}

async function loginScreen(): Promise<void> {
  const screen = element('main', 'login-screen');
  const story = element('section', 'login-story');
  story.append(element('span', 'wordmark', 'AiMo'), element('span', 'eyebrow', text.eyebrow), element('h1', '', text.login.storyHeading), element('p', '', text.login.storyBody));
  const sketch = element('div', 'login-sketch');
  for (const [className, title, caption] of [['sketch-reference', text.referenceTitle, text.login.sketchReference], ['sketch-design', text.designTitle, text.login.sketchDesign], ['sketch-note', text.iterationTitle, text.login.sketchIteration]]) { const card = element('div', className); card.append(element('span', '', title), element('strong', '', caption)); sketch.append(card); }
  story.append(sketch);
  const panel = element('section', 'login-panel'); const form = element('form', 'login-form');
  const account = element('input', 'input'); account.name = 'username'; account.autocomplete = 'username'; account.required = true;
  const credential = element('input', 'input'); credential.name = 'password'; credential.type = 'password'; credential.autocomplete = 'current-password'; credential.required = true;
  const error = element('p', 'form-error'); error.setAttribute('role', 'alert');
  error.textContent = loginErrorText();
  const submit = element('button', 'button primary login-submit', text.login.submit); submit.type = 'submit';
  const backHomeBtn = element('button', 'button outline', text.login.backHome);
  backHomeBtn.type = 'button';
  backHomeBtn.style.marginTop = '8px';
  backHomeBtn.addEventListener('click', () => { takePendingStart(); history.pushState(null, '', '#landing'); void showUnifiedHome(); });
  form.append(element('span', 'eyebrow', text.welcomeTitle), element('h2', '', text.login.welcome), element('p', 'form-description', text.login.description), field(text.login.account, account), field(text.login.password, credential), error, submit, backHomeBtn);
  panel.append(form); screen.append(story, panel); mount.replaceChildren(screen);
  const providers = await api.request<{ google: boolean; password: boolean }>('authProviders').catch(() => ({ google: false, password: true }));
  if (providers.google) { const google = googleLoginButton(api, message => { error.textContent = message; }); form.insertBefore(google, account.parentElement!); }
  if (!providers.password) { account.parentElement!.remove(); credential.parentElement!.remove(); submit.remove(); }
  if (!providers.google && !providers.password) error.textContent = text.login.unavailable;
  form.addEventListener('submit', (event) => { event.preventDefault(); submit.disabled = true; error.textContent = ''; void api.request<AuthSession>('login', { body: { accountName: account.value.trim(), credential: credential.value } }).then(async (session) => { api.session = session; credential.value = ''; await afterLogin(); }).catch((failure: unknown) => { error.textContent = failure instanceof Error ? errorMessage(failure) : text.login.incomplete; submit.disabled = false; }); });
}

async function showUnifiedHome(): Promise<void> {
  history.replaceState(null, '', '#projects');
  projectEpoch++; disposeCanvas();
  state.project = null; state.boardId = null;
  if (!api.session) state.projects = [];
  else {
    try { state.projects = (await api.request<ProjectList>('listProjects')).projects; }
    catch (error) { report(error); }
  }
  appRoot.showLanding(mount, { projects: state.projects, accountName: api.session ? api.session.principal.displayName : null, api: api.session ? api : null, callbacks: {
    onOpenProject: projectId => { void requireSession({ kind: 'open', projectId }).catch(report); },
    onCreateProject: name => requireSession({ kind: 'create', name }),
    onStartDesign: (intent, title) => requireSession({ kind: 'create', name: title, intent }),
    onLogout: () => { void logout(); },
    onLogin: () => { void loginScreen(); },
  } });
}

/** Runs a home-page start once; the request survives a login round trip, including Google redirects. */
async function runStart(start: PendingStart): Promise<void> {
  if (starting) return;
  starting = true;
  try {
    if (start.kind === 'open') { await openProject(start.projectId); return; }
    const project = await api.request<Project>('createProject', { body: { name: start.name } });
    state.projects.unshift(project);
    await openProject(project.projectId, undefined, false, start.intent);
  } finally { starting = false; }
}

function requireSession(start: PendingStart): Promise<void> {
  if (api.session) return runStart(start);
  try { sessionStorage.setItem(PENDING_START_KEY, JSON.stringify(start)); } catch { /* the start is simply not resumed */ }
  return loginScreen();
}

function takePendingStart(): PendingStart | undefined {
  try {
    const raw = sessionStorage.getItem(PENDING_START_KEY); sessionStorage.removeItem(PENDING_START_KEY);
    const value: unknown = raw ? JSON.parse(raw) : undefined;
    if (!value || typeof value !== 'object') return undefined;
    const start = value as Record<string, unknown>;
    if (start.kind === 'open' && typeof start.projectId === 'string') return { kind: 'open', projectId: start.projectId };
    if (start.kind === 'create' && typeof start.name === 'string') return { kind: 'create', name: start.name, ...(typeof start.intent === 'string' ? { intent: start.intent } : {}) };
  } catch { /* ignore unreadable pending starts */ }
  return undefined;
}

async function afterLogin(): Promise<void> {
  const requestId = deviceAuthorizationId();
  if (requestId) { await showDeviceAuthorization(api, mount, requestId, logout); return; }
  if (location.pathname === '/connect') { mount.replaceChildren(element('p', 'boot-screen', text.invalidAuthorization)); return; }
  await startWorkspace();
}

async function startWorkspace(): Promise<void> {
  state.projects = (await api.request<ProjectList>('listProjects')).projects;
  const resume = takePendingStart();
  if (resume) { await runStart(resume); return; }
  if (location.hash === '#landing') { await showUnifiedHome(); return; }
  let linked = false;
  try { linked = !!readWorkbenchLocation(location.hash); } catch (error) { report(error); }
  if (linked) { await restoreWorkbenchLocation().catch(report); return; }
  await showUnifiedHome();
}

async function openProject(projectId: string, boardId?: string, preserveLocation = false, initialIntent?: string): Promise<void> {
  const epoch = ++projectEpoch;
  disposeCanvas();
  const transition = playStitchTransition(state.projects.find(p => p.projectId === projectId)?.name || text.canvas);
  try {
    const project = await api.request<Project>('getProject', { params: { id: projectId } }); if (epoch !== projectEpoch) return;
    transition.updateMessage(text.preparing);
    const board = await api.request<Board>('getBoard', { params: { id: boardId ?? project.defaultBoardId } }); if (epoch !== projectEpoch) return;
    if (board.projectId !== projectId) throw new Error(text.wrongProject);
    notice.hidden = true; state.project = project; state.boardId = board.boardId;
    if (!preserveLocation) history.pushState(null, '', workbenchUrl(location.href, { projectId, boardId: board.boardId }));
    await mountProjectCanvas(epoch, project, board.boardId, initialIntent);
  } finally {
    await transition.finish();
  }
}

/** Opens the project canvas a link names. Old node and task links are not resolved; the canvas copies canonical resource links. */
async function restoreWorkbenchLocation(): Promise<void> {
  const desired = readWorkbenchLocation(location.hash); if (!desired || !api.session) return;
  if (state.project?.projectId !== desired.projectId || state.boardId !== desired.boardId || !designCanvas) await openProject(desired.projectId, desired.boardId, true);
  if (desired.cardId || desired.runId) {
    history.replaceState(null, '', workbenchUrl(location.href, { projectId: desired.projectId, boardId: desired.boardId }));
    showNotice(text.legacyLink);
  }
}

async function logout(): Promise<void> {
  await designCanvas?.controller.flush().catch(() => undefined);
  await api.request('logout'); projectEpoch++; disposeCanvas();
  api.session = null; state.projects = []; state.project = null; state.boardId = null;
  const { clearPdfPreviews } = await import('./pdf-preview.js'); clearPdfPreviews();
  await showUnifiedHome();
}

function installShortcuts(): void {
  document.addEventListener('keydown', (event) => {
    if (!designCanvas || !(event.ctrlKey || event.metaKey) || event.isComposing || document.querySelector('dialog[open]')) return;
    if (event.key.toLowerCase() === 's') { event.preventDefault(); void designCanvas.controller.flush().catch(report); }
  });
  window.addEventListener('beforeunload', (event) => { if (designCanvas?.controller.hasUnsavedChanges) event.preventDefault(); });
}

async function boot(): Promise<void> {
  applyDocumentLocale();
  installShortcuts();
  window.addEventListener('popstate', () => {
    if (location.hash === '#landing') { void showUnifiedHome(); return; }
    if (location.hash === '#projects' || location.hash === '#dashboard' || location.hash === '') { void showUnifiedHome(); return; }
    void restoreWorkbenchLocation().catch(report);
  });
  window.addEventListener('hashchange', () => { if (readWorkbenchLocationSafe()) void restoreWorkbenchLocation().catch(report); });
  try {
    api.session = await api.request<AuthSession>('getSession');
    await afterLogin();
  } catch (error) {
    await showUnifiedHome();
    if (error instanceof ApiFailure && error.status !== 401 && error.status !== 403) mount.append(element('p', 'login-offline', errorMessage(error)));
  }
}

function readWorkbenchLocationSafe(): boolean { try { return !!readWorkbenchLocation(location.hash); } catch { return false; } }

void boot();
